import ReactDOM from 'react-dom/client';
import Root from './Root';
import * as serviceWorker from './serviceWorker';

const rootEl = document.getElementById('root') as Element;
const CHUNK_RECOVERY_KEY = 'matflixx:chunk-recovery';
const environment = process.env.REACT_APP_ENVIRONMENT as string;
const isProdLike = ['production', 'staging'].includes(environment);
const sentryDsn = process.env.REACT_APP_SENTRY_DSN || '';
const sentryEnabled = (process.env.REACT_APP_SENTRY_ENABLED || 'true') === 'true';
const sentryRawRelease =
	process.env.REACT_APP_SENTRY_RELEASE || process.env.REACT_APP_RELEASE || process.env.REACT_APP_VERSION || process.env.REACT_APP_COMMIT_SHA || '';
const sentryRelease = sentryRawRelease
	? sentryRawRelease.includes('@')
		? sentryRawRelease
		: `matflixx-portal-frontend@${sentryRawRelease}`
	: undefined;
const sentryDist = process.env.REACT_APP_SENTRY_DIST || undefined;
const otlpEndpoint = process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT || '';

const getTraceSampleRate = (): number => {
	const configuredRate = Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE);
	if (!Number.isNaN(configuredRate) && configuredRate >= 0 && configuredRate <= 1) {
		return configuredRate;
	}
	return isProdLike ? 0.1 : 0.0;
};

const buildChunkRecoveryKey = () => {
	if (typeof window === 'undefined') return CHUNK_RECOVERY_KEY;
	return `${CHUNK_RECOVERY_KEY}:${window.location.pathname}`;
};

const attemptChunkRecovery = (reason: string) => {
	if (typeof window === 'undefined') return;

	const recoveryKey = buildChunkRecoveryKey();
	if (sessionStorage.getItem(recoveryKey) === 'done') {
		return;
	}

	sessionStorage.setItem(recoveryKey, 'done');
	console.warn('Recovering from stale frontend chunk', reason);

	const nextUrl = new URL(window.location.href);
	nextUrl.searchParams.set('_chunk_reload', String(Date.now()));
	window.location.replace(nextUrl.toString());
};

const isChunkLoadMessage = (message: string) => /Loading (CSS )?chunk [0-9]+ failed/i.test(message) || /ChunkLoadError/i.test(message);

const isRecoverableStaticAsset = (filename?: string) => typeof filename === 'string' && /\/static\/(?:js|css)\/.+\.(?:js|css)($|\?)/.test(filename);

const isChunkSyntaxError = (message: string, filename?: string) =>
	typeof message === 'string' &&
	isRecoverableStaticAsset(filename) &&
	(message.includes("Unexpected token '<'") || message.includes('Unexpected EOF'));

const registerChunkRecovery = () => {
	if (typeof window === 'undefined') return;

	window.addEventListener(
		'unhandledrejection',
		(event) => {
			const reason = event.reason;
			const message = typeof reason === 'string' ? reason : reason?.message || '';
			if (isChunkLoadMessage(message)) {
				attemptChunkRecovery(message);
			}
		},
		true
	);

	window.addEventListener(
		'error',
		(event) => {
			const target = event.target as HTMLElement | null;
			if (target instanceof HTMLScriptElement && isRecoverableStaticAsset(target.src)) {
				attemptChunkRecovery(`script-load:${target.src}`);
				return;
			}

			if (target instanceof HTMLLinkElement && isRecoverableStaticAsset(target.href)) {
				attemptChunkRecovery(`style-load:${target.href}`);
				return;
			}

			if (typeof event.message === 'string' && isChunkLoadMessage(event.message)) {
				attemptChunkRecovery(event.message);
				return;
			}

			if (typeof event.message === 'string' && isChunkSyntaxError(event.message, event.filename)) {
				attemptChunkRecovery(`${event.message}:${event.filename}`);
			}
		},
		true
	);
};

const initObservability = async () => {
	const shouldInitTelemetry = Boolean(otlpEndpoint) && isProdLike;
	if (shouldInitTelemetry) {
		try {
			await import('./telemetry');
		} catch (error) {
			console.error('Telemetry initialization failed', error);
		}
	}

	const allowedSentryEnvs = ['production', 'staging', 'development', 'local'];
	const shouldInitSentry = allowedSentryEnvs.includes(environment) && sentryEnabled && Boolean(sentryDsn);
	if (!shouldInitSentry) {
		return;
	}

	try {
		const Sentry = await import('@sentry/react');
		const { BrowserTracing } = await import('@sentry/tracing');
		Sentry.init({
			dsn: sentryDsn,
			integrations: [new BrowserTracing()],
			tracesSampleRate: getTraceSampleRate(),
			sendDefaultPii: true,
			release: sentryRelease,
			dist: sentryDist,
			beforeSend(event) {
				if (typeof window !== 'undefined') {
					const url = new URL(window.location.href);
					const cleanParams: Record<string, string> = {};
					const sensitiveKeys = ['token', 'auth', 'password', 'access_token'];

					for (const [key, value] of url.searchParams.entries()) {
						if (!sensitiveKeys.includes(key.toLowerCase())) {
							cleanParams[key] = value;
						}
					}

					event.tags = {
						...(event.tags || {}),
						page_url: window.location.href.split('?')[0]
					};

					event.extra = {
						...(event.extra || {}),
						queryParams: cleanParams
					};
				}

				const logentryMessage =
					event.logentry && 'formatted' in event.logentry ? (event.logentry as { formatted?: string }).formatted : undefined;
				const eventMessage = event.message || logentryMessage || event.exception?.values?.[0]?.value || '';
				const pageUrl =
					(typeof event.tags?.page_url === 'string' && event.tags.page_url) ||
					(typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');
				const isLogin401Noise =
					pageUrl.endsWith('/login') && typeof eventMessage === 'string' && eventMessage.includes('Request failed with status code 401');

				if (isLogin401Noise) {
					return null;
				}

				return event;
			}
		});
	} catch (error) {
		console.error('Sentry initialization failed', error);
	}
};

registerChunkRecovery();
const root = ReactDOM.createRoot(rootEl);
// Wrap your component in StrictMode to activate additional checks and warnings
root.render(<Root />);
void initObservability();

serviceWorker.unregister();
